import { useIdentity } from '@vp/auth-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api-config';
import { IApiHooksWithTrigger, IIdentityObject } from '../../interfaces/app-interfaces';
import { getChatbotToken, getRestId } from '../../utils/session-storage-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { useCustomTracking } from '../custom-hooks/useTracking';

export const useCBChatEscalate = (): IApiHooksWithTrigger<string> => {
  const { logCustomAttributesNR } = useNRLogging();

  const { sendTrackingEvent } = useCustomTracking();

  const {
    state: { customFlow },
  } = useContext(ChatbotContext);

  const [data, setData] = useState<string>();
  const identity = useIdentity();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [toggle, setToggle] = useState<boolean>(false);

  const trigger = useCallback(() => {
    setToggle((prev) => !prev);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (toggle) {
        setLoading(true);
        try {
          const { authorizationHeader, canonicalId } = identity as IIdentityObject;
          const chatbotToken = getChatbotToken();
          const myHeaders = new Headers();
          myHeaders.append('Authorization', authorizationHeader || '');
          myHeaders.append('Content-Type', 'application/json');
          myHeaders.append('Chatbot-Authorization', `Bearer ${chatbotToken}`);

          const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify({
              agent_name: 'BOT',
              request_id: getRestId() || '',
              shopper_id: canonicalId || '',
              channel: 'chat',
            }),
            redirect: 'follow' as RequestRedirect,
          };
          logCustomAttributesNR('ca-chatbot-api-request', {
            logLevel: 'info',
            message: 'Escalating chatbot session',
            apiType: 'escalateChatbotSession',
          });
          /** When escalating, if flow is going on, send an event */
          if (customFlow && customFlow.flowType && customFlow.flowStep) {
            sendTrackingEvent('Chatbot Clicked', 'Chatbot:OrderFlow', 'chatbotFlowEscalated', 'GEN AI Chatbot', {
              chatbotFlowId: customFlow.flowType,
              chatbotFlowStep: customFlow.flowStep,
            });
          }
          fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/escalate-chat`, requestOptions)
            .then((response) => {
              if (!response.ok) {
                // If response is not ok (status not in the range 200-299),
                // throw an error to skip the next then block
                const error = new Error(`API responded with status: ${response.status}`);
                error.name = 'ApiError';
                throw error;
              }
              return response.json();
            })
            .then((result) => {
              console.log(result);
              logCustomAttributesNR('ca-chatbot-api-response', {
                logLevel: 'info',
                message: 'Escalating chatbot session API success',
                apiType: 'escalateChatbotSession',
              });
              setData(result.message || 'Session escalated successfully');
              setError(undefined);
            })
            .catch((error) => {
              setError(error as Error);
              console.error(error);
              logCustomAttributesNR('ca-chatbot-api-error', {
                errorName: error.name,
                errorStack: error.stack,
                message: error.message,
                logLevel: 'error',
                apiType: 'escalateChatbotSession',
              });
            })
            .finally(() => {
              setLoading(false);
              setToggle(false); // Reset toggle after completion
            });
        } catch (err) {
          setError(err as Error);
        }
      }
    };
    fetchData();
  }, [toggle]);

  return {
    data,
    loading,
    error,
    trigger,
  };
};
